'use client';

import { useEffect, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { usePathname } from '@/i18n/navigation';
import {
  LOCALES,
  LOCALE_COOKIE,
  LOCALE_COOKIE_MAX_AGE_SECONDS,
  localeDefinition,
  toLocale,
} from '@/i18n/config';
import { localizePath } from '@/i18n/detect';

/**
 * The language dropdown — on every page, always, even when detection got it
 * right (Documentation/13 §2.3).
 *
 * A native <select> listing each language in its own name. Choosing one is
 * the ONLY place the preference cookie is written: an explicit choice
 * persists for a year and overrides browser-language detection on the next
 * visit. The current query string rides along, so a filtered standings view
 * survives a language change. The visible label is the language's own name
 * (Español, not "Spanish"), which is what a reader hunting for their language
 * actually recognises.
 */
export function LanguageSwitcher() {
  const locale = toLocale(useLocale());
  const pathname = usePathname();
  const t = useTranslations('components.languageSwitcher');
  const [isOpen, setIsOpen] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);

  // One language is not a choice; render nothing rather than a lone option.
  if (LOCALES.length < 2) return null;

  useEffect(() => {
    function closeOnOutsidePointer(event: MouseEvent) {
      if (!switcherRef.current?.contains(event.target as Node)) setIsOpen(false);
    }

    document.addEventListener('mousedown', closeOnOutsidePointer);
    return () => document.removeEventListener('mousedown', closeOnOutsidePointer);
  }, []);

  function choose(code: string) {
    if (code === locale) return;
    try {
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = `${LOCALE_COOKIE}=${code}; Path=/; Max-Age=${LOCALE_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
    } catch {
      // Cookies blocked: the navigation still happens; the choice just does not persist.
    }
    const href = localizePath(pathname, code);
    window.location.assign(`${href}${window.location.search}${window.location.hash}`);
  }

  return (
    <div className="lang-switch" ref={switcherRef}>
      <button
        type="button"
        className="lang-trigger"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label={t('label')}
        onClick={() => setIsOpen((open) => !open)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setIsOpen(false);
        }}
      >
        <span>{t('trigger')}</span>
        <img
          src="/cs/language-arrow.svg"
          className="lang-trigger-arrow"
          alt=""
          aria-hidden="true"
        />
      </button>
      {isOpen && (
        <div className="lang-menu" role="menu" aria-label={t('label')}>
          {LOCALES.map((code) => (
            <button
              key={code}
              type="button"
              className={code === locale ? 'lang-option is-current' : 'lang-option'}
              role="menuitem"
              aria-current={code === locale ? 'true' : undefined}
              lang={code}
              onClick={() => choose(code)}
            >
              {localeDefinition(code).label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
